import {useQuery, type UseQueryOptions} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'

import type {IssueField} from '../../api/common-contracts'
import {apiGetSuggestedIssueFields} from '../../api/memex/api-get-suggested-issue-fields'
import type {GetSuggestedIssueFieldsResponse} from '../../api/memex/contracts'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {queryClient} from '../../queries/query-client'
import {useAllColumns} from '../columns/use-all-columns'
import {useIssueFieldsEnabled} from './use-issue-fields-enabled'

/** Reload issue field suggestions after 5 minutes */
export const SuggestionsCacheMs = 5 * 60 * 1000

const IssueFieldsQueryKey = ['issue-fields', 'suggested-issue-fields']

function useIssueFieldsQueryOptions(): UseQueryOptions<GetSuggestedIssueFieldsResponse> {
  const {hasWritePermissions} = ViewerPrivileges()
  const issueFieldsEnabled = useIssueFieldsEnabled()

  const enabled = issueFieldsEnabled && hasWritePermissions

  const queryFn = useCallback(async () => {
    return apiGetSuggestedIssueFields()
  }, [])

  return useMemo(
    () => ({
      queryKey: IssueFieldsQueryKey,
      queryFn,
      enabled,
      staleTime: SuggestionsCacheMs,
    }),
    [queryFn, enabled],
  )
}

export interface IssueFieldsQueryResult {
  issueFields: Array<IssueField>
  hasOrgOnlyFieldsHidden: boolean
  hasAllAvailableIssueFieldsAdded: boolean
}

export function useIssueFieldsQuery() {
  const queryOptions = useIssueFieldsQueryOptions()
  const {allColumns} = useAllColumns()

  const usedIssueFieldSet = useMemo(() => {
    return new Set(allColumns.filter(col => col.issueFieldId).map(col => col.issueFieldId))
  }, [allColumns])

  const select = useCallback(
    (data: GetSuggestedIssueFieldsResponse): IssueFieldsQueryResult => {
      const availableIssueFields = data.issue_fields.filter(field => !usedIssueFieldSet.has(field.id))

      return {
        issueFields: availableIssueFields,
        hasOrgOnlyFieldsHidden: data.has_org_only_fields_hidden ?? false,
        hasAllAvailableIssueFieldsAdded: data.issue_fields.length > 0 && availableIssueFields.length === 0,
      }
    },
    [usedIssueFieldSet],
  )
  const useQueryOptions = useMemo(() => {
    return {
      ...queryOptions,
      select,
    }
  }, [select, queryOptions])

  return useQuery(useQueryOptions)
}

export function usePrefetchIssueFieldsQuery() {
  const queryOptions = useIssueFieldsQueryOptions()

  return useCallback(() => {
    const {enabled, ...restOptions} = queryOptions
    if (!enabled) return
    const state = queryClient.getQueryState(restOptions.queryKey)

    // If we've made several attempts to fetch the data and it keeps failing, don't keep trying
    const skipPrefetch = state && state.status === 'error' && state.errorUpdateCount >= 3
    if (skipPrefetch) return

    // Enabled is not a supported option for prefetching
    return queryClient.prefetchQuery(restOptions)
  }, [queryOptions])
}

export function useInvalidateIssueFieldQuery() {
  const queryOptions = useIssueFieldsQueryOptions()

  return useCallback(() => {
    const {enabled} = queryOptions
    if (!enabled) return

    return queryClient.invalidateQueries({
      queryKey: IssueFieldsQueryKey,
    })
  }, [queryOptions])
}
